import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageTitle } from "@/components/ui/PageTitle";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { Overview } from "@/components/owner/Overview";
import { Products } from "@/components/owner/Products";
import { useState } from "react";
import { InterfaceModel } from "@/types/interface";
import { toast } from "sonner";

const OwnerDashboard = () => {
  const location = useLocation();
  const isProductsPage = location.pathname === "/owner/products";
  const [sampleData, setSampleData] = useState([]);

  const { data: ownerData } = useQuery({
    queryKey: ["ownerData"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      console.log("Fetching owner data for user:", user.id);

      const { data, error } = await supabase
        .from("owner_data")
        .select(`
          *,
          business_types (
            id,
            name,
            description
          )
        `)
        .eq("owner_id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: interfaceModel } = useQuery<InterfaceModel>({
    queryKey: ["businessInterface", ownerData?.business_type_id],
    queryFn: async () => {
      if (!ownerData?.business_type_id) return null;
      
      console.log("Fetching interface model for business type:", ownerData.business_type_id);
      
      const { data, error } = await supabase
        .from("interface_models")
        .select(`
          *,
          business_types!interface_models_business_type_id_fkey (
            id,
            name,
            description
          )
        `)
        .eq("business_type_id", ownerData.business_type_id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        return {
          ...data,
          layout: typeof data.layout === 'string' ? JSON.parse(data.layout) : data.layout
        };
      }
      return null;
    },
    enabled: !!ownerData?.business_type_id,
  });

  // Charger les produits spécifiques au propriétaire
  const { data: stockItems, isLoading: isLoadingStock, error: stockError } = useQuery({
    queryKey: ["stockItems", ownerData?.business_type_id],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error("No user found");
          throw new Error("Non authentifié");
        }

        console.log("Current user ID:", user.id);
        console.log("Business type ID:", ownerData?.business_type_id);

        // Récupérer le stock de l'entreprise
        const { data: businessStock, error: stockError } = await supabase
          .from('business_stocks')
          .select('*')
          .eq('business_id', ownerData.business_type_id)
          .eq('owner_id', user.id)
          .maybeSingle();

        if (stockError) {
          console.error("Error fetching business stock:", stockError);
          // Si aucun stock n'existe, en créer un
          const { data: newStock, error: createError } = await supabase
            .from('business_stocks')
            .insert({
              business_id: ownerData.business_type_id,
              name: 'Stock Principal',
              owner_id: user.id
            })
            .select()
            .single();

          if (createError) {
            console.error("Error creating stock:", createError);
            toast.error("Erreur lors de la création du stock");
            throw createError;
          }

          console.log("New stock created:", newStock);
          return [];
        }

        if (!businessStock) {
          console.log("No business stock found, creating one");
          const { data: newStock, error: createError } = await supabase
            .from('business_stocks')
            .insert({
              business_id: ownerData.business_type_id,
              name: 'Stock Principal',
              owner_id: user.id
            })
            .select()
            .single();

          if (createError) {
            console.error("Error creating stock:", createError);
            toast.error("Erreur lors de la création du stock");
            throw createError;
          }

          console.log("New stock created:", newStock);
          return [];
        }

        console.log("Found business stock:", businessStock);

        // Récupérer les articles du stock
        const { data: items, error: itemsError } = await supabase
          .from('stock_items')
          .select('*')
          .eq('stock_id', businessStock.id);

        if (itemsError) {
          console.error("Error fetching stock items:", itemsError);
          toast.error("Erreur lors de la récupération des articles");
          throw itemsError;
        }

        console.log("Found items:", items?.length || 0);
        return items || [];
      } catch (error: any) {
        console.error("Unexpected error in stock items query:", error);
        toast.error("Une erreur inattendue s'est produite");
        throw error;
      }
    },
    enabled: !!ownerData?.business_type_id,
  });

  // Mettre à jour sampleData quand stockItems change
  if (stockItems && JSON.stringify(sampleData) !== JSON.stringify(stockItems)) {
    setSampleData(stockItems);
  }

  if (stockError) {
    console.error("Error loading stock:", stockError);
    toast.error("Erreur lors du chargement du stock");
  }

  const handleUpdateStock = (newData: any[]) => {
    setSampleData(newData);
  };

  return (
    <DashboardLayout userType="owner">
      {isProductsPage ? (
        <Products
          sampleData={sampleData}
          onUpdateStock={handleUpdateStock}
        />
      ) : (
        <>
          <PageTitle
            title="Tableau de bord"
            subtitle="Gérez votre entreprise"
          />
          <Overview
            ownerData={ownerData}
            interfaceModel={interfaceModel}
            sampleData={sampleData}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default OwnerDashboard;